import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Webhook, Plus, Trash2, Power, PowerOff, ExternalLink, Clock } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const availableEvents = [
  { id: 'deployment.created', label: 'Deployment Created' },
  { id: 'deployment.started', label: 'Deployment Started' },
  { id: 'deployment.completed', label: 'Deployment Completed' },
  { id: 'deployment.failed', label: 'Deployment Failed' },
  { id: 'deployment.rollback', label: 'Deployment Rollback' },
];

export default function WebhooksPage() {
  const { getAuthHeaders } = useAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [],
    secret: ''
  });

  const fetchWebhooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/webhooks`, {
        headers: getAuthHeaders()
      });
      setWebhooks(response.data);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, [getAuthHeaders]);

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    if (newWebhook.events.length === 0) {
      toast.error('Please select at least one event');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/webhooks`, newWebhook, {
        headers: getAuthHeaders()
      });
      setWebhooks([...webhooks, response.data]);
      setCreateDialogOpen(false);
      setNewWebhook({ name: '', url: '', events: [], secret: '' });
      toast.success('Webhook created successfully!');
    } catch (error) {
      toast.error('Failed to create webhook');
    }
  };

  const handleToggleWebhook = async (webhookId) => {
    try {
      const response = await axios.put(`${API_URL}/webhooks/${webhookId}/toggle`, {}, {
        headers: getAuthHeaders()
      });
      setWebhooks(webhooks.map(w => 
        w.id === webhookId ? { ...w, active: response.data.active } : w
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to toggle webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await axios.delete(`${API_URL}/webhooks/${webhookId}`, {
        headers: getAuthHeaders()
      });
      setWebhooks(webhooks.filter(w => w.id !== webhookId));
      toast.success('Webhook deleted');
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const toggleEvent = (eventId) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="webhooks-title">
              Webhooks
            </h1>
            <p className="text-foreground/60">Integrate with external services via webhooks</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-bold hover:shadow-neon-cyan" data-testid="create-webhook-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Webhook
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg" aria-describedby="create-webhook-description">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Create Webhook</DialogTitle>
                <p id="create-webhook-description" className="sr-only">Form to create a new webhook integration</p>
              </DialogHeader>
              <form onSubmit={handleCreateWebhook} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Webhook Name</Label>
                  <Input
                    value={newWebhook.name}
                    onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                    placeholder="e.g., Slack Notifications"
                    className="bg-background border-border"
                    required
                    data-testid="webhook-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Endpoint URL</Label>
                  <Input
                    type="url"
                    value={newWebhook.url}
                    onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                    placeholder="https://your-service.com/webhook"
                    className="bg-background border-border"
                    required
                    data-testid="webhook-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Secret (Optional)</Label>
                  <Input
                    type="password"
                    value={newWebhook.secret}
                    onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
                    placeholder="Webhook signing secret"
                    className="bg-background border-border"
                    data-testid="webhook-secret-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Events to Subscribe</Label>
                  <div className="grid grid-cols-1 gap-2 p-4 bg-[#0A0E27] rounded-lg border border-border">
                    {availableEvents.map((event) => (
                      <div key={event.id} className="flex items-center gap-3">
                        <Checkbox
                          id={event.id}
                          checked={newWebhook.events.includes(event.id)}
                          onCheckedChange={() => toggleEvent(event.id)}
                        />
                        <label
                          htmlFor={event.id}
                          className="text-sm text-foreground/80 cursor-pointer"
                        >
                          {event.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary text-black font-bold" data-testid="create-webhook-submit">
                  Create Webhook
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Webhooks List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : webhooks.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-16 text-center">
              <Webhook className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-heading font-semibold text-white mb-2">No Webhooks Yet</h3>
              <p className="text-muted-foreground mb-6">Create webhooks to integrate with external services</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-primary text-black font-bold">
                <Plus className="w-5 h-5 mr-2" />
                Create Webhook
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {webhooks.map((webhook, i) => (
              <motion.div
                key={webhook.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-card/50 border-border hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${webhook.active ? 'bg-accent/10' : 'bg-muted'}`}>
                          <Webhook className={`w-6 h-6 ${webhook.active ? 'text-accent' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-semibold text-white text-lg">{webhook.name}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              webhook.active ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'
                            }`}>
                              {webhook.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <ExternalLink className="w-4 h-4" />
                            <span className="font-code text-xs truncate max-w-md">{webhook.url}</span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {webhook.events?.map((event) => (
                              <span
                                key={event}
                                className="px-2 py-0.5 bg-secondary/20 text-secondary rounded text-xs"
                              >
                                {event.replace('deployment.', '')}
                              </span>
                            ))}
                          </div>
                          {webhook.last_triggered && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Last triggered: {new Date(webhook.last_triggered).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleWebhook(webhook.id)}
                          className={webhook.active 
                            ? 'border-yellow-500 text-yellow-500 hover:bg-yellow-500/10' 
                            : 'border-accent text-accent hover:bg-accent/10'
                          }
                        >
                          {webhook.active ? (
                            <>
                              <PowerOff className="w-4 h-4 mr-1" />
                              Disable
                            </>
                          ) : (
                            <>
                              <Power className="w-4 h-4 mr-1" />
                              Enable
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteWebhook(webhook.id)}
                          className="border-destructive text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
